import { FC, useState } from "react";
import { Box, Divider, Typography } from "@material-ui/core";
import { CheckCircle as CheckCircleIcon } from "@material-ui/icons";
import moment from "moment";
import { usePostHog } from "posthog-js/react";

import { TermsAndConditions } from "components";
import { Button, ErrorMessage, Modal } from "components";
import { useAssetDetailData } from "containers/AssetDetail/hooks";
import { formatCurrencyAmount } from "lib/format";
import { parseNumber } from "lib/parse";
import { useAppConfigs } from "services/appConfigs";
import { DEFAULT_ERROR_MESSAGE } from "services/constants";
import { setBuyNowPrice } from "services/listings";
import { ResaleInfo } from "services/listings/types";
import { makeCounteroffer } from "services/offers";
import { ListingOffer } from "services/offers/types";
import theme from "theme";
import { circleIconSize } from "theme/constants";

import TransactionFeeNotice from "../TransactionFeeNotice";
import MakeCounterofferForm from "./MakeCounterofferForm";

const TitleByStep: FC<{
  step: number;
  isOwner: boolean;
  offer: ListingOffer;
  royxTake: number;
}> = ({ step, isOwner, offer, royxTake }) =>
  step === 0 ? (
    <Box textAlign="center">
      <Box fontSize={18} fontWeight="400">
        {isOwner ? "Offer" : "Current Offer"}
      </Box>
      <Box fontSize={18} fontWeight={theme.typography.fontWeightMedium} mb={2}>
        {formatCurrencyAmount("USD", offer.amount)}
      </Box>
      {isOwner && (
        <>
          <Box fontSize={18} fontWeight="400">
            Your Sale Proceeds
          </Box>
          <Box fontSize={18} fontWeight={theme.typography.fontWeightMedium}>
            {formatCurrencyAmount("USD", Number(offer.amount) * (1 - royxTake))}
          </Box>
        </>
      )}
    </Box>
  ) : (
    <>{titleByStep[step]}</>
  );
interface MakeCounterofferModalProps {
  buyNowPrice: number;
  highestOpenBuyerOfferAmount: number;
  isAcceptingFinalOffersEnabled: boolean;
  isOwner: boolean;
  listingId: number;
  ltm: number;
  minimumPrice: number;
  offer: ListingOffer;
  offers: ListingOffer[];
  onClose: () => void;
  onRejectOffer: (offer: ListingOffer) => void;
  resaleInfo: ResaleInfo;
  royxTake: number;
  valuationId: number;
}

const titleByStep = [
  "Make Counteroffer",
  "Confirm Counteroffer",
  "Counteroffer Sent",
];

const MakeCounterofferModal: FC<MakeCounterofferModalProps> = ({
  buyNowPrice,
  highestOpenBuyerOfferAmount,
  isAcceptingFinalOffersEnabled,
  isOwner,
  listingId,
  ltm,
  minimumPrice,
  offer,
  offers,
  onClose,
  onRejectOffer,
  resaleInfo,
  royxTake,
  valuationId,
}) => {
  const initialMakeOfferValues = {
    amount: "",
    updateListPrice: false,
  };
  const { handleRefreshListing, listing } = useAssetDetailData(listingId);
  const posthog = usePostHog();

  const appConfigsQueries = useAppConfigs(["OFFER_ACCEPTANCE_DURATION"]);
  const humanizedOfferExpirationDate =
    moment
      .duration(appConfigsQueries.data?.OFFER_ACCEPTANCE_DURATION)
      .humanize({ h: 73 }) || "";

  const [step, setStep] = useState(0);
  const [formValues, setFormValues] = useState(initialMakeOfferValues);
  const [errorMessage, setErrorMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const handleClose = () => {
    setErrorMessage("");
    onClose();
  };

  const makeOnBackHandler = () =>
    step === 1
      ? () => {
          setErrorMessage("");
          setStep(0);
        }
      : undefined;

  const handleMakeCounteroffer = async () => {
    try {
      setIsLoading(true);
      setErrorMessage("");
      await makeCounteroffer(
        listingId,
        offer.id,
        {
          amount: parseNumber(formValues.amount),
          valuation_id: valuationId,
        },
        posthog,
      );
      if (formValues.updateListPrice) {
        await setBuyNowPrice(
          listingId,
          {
            buy_now_price: parseNumber(formValues.amount),
          },
          posthog,
        );
      }
      setStep(2);
    } catch (e) {
      setErrorMessage(e instanceof Error ? e.message : DEFAULT_ERROR_MESSAGE);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Modal
      title={
        <TitleByStep
          step={step}
          isOwner={isOwner}
          offer={offer}
          royxTake={royxTake}
        />
      }
      open
      onClose={handleClose}
      onBack={makeOnBackHandler()}
    >
      {step === 0 && (
        <>
          <Divider />
          <Box>
            <Typography component="div">
              <Box
                mt={2}
                fontSize={18}
                fontWeight={theme.typography.fontWeightMedium}
                textAlign="center"
              >
                {titleByStep[step]}
              </Box>
            </Typography>
            <MakeCounterofferForm
              buyNowPrice={buyNowPrice}
              highestOpenBuyerOfferAmount={highestOpenBuyerOfferAmount}
              humanizedOfferExpirationDate={humanizedOfferExpirationDate}
              initialValues={formValues}
              isAcceptingFinalOffersEnabled={isAcceptingFinalOffersEnabled}
              isOwner={isOwner}
              listingKind={listing?.kind}
              ltm={ltm}
              minimumPrice={minimumPrice}
              offer={offer}
              offers={offers}
              onSave={async v => {
                setFormValues(v);
                setErrorMessage("");
                await handleRefreshListing();
              }}
              onSaveSuccess={() => setStep(s => s + 1)}
              resaleInfo={resaleInfo}
              royxTake={royxTake}
            />
            <Button
              customColor="red"
              size="large"
              fullWidth
              variant="text"
              onClick={() => onRejectOffer(offer)}
            >
              Reject Offer
            </Button>
          </Box>
        </>
      )}
      {step === 1 && (
        <>
          <Typography component="div">
            <Box fontSize={15} textAlign="center" mx={2}>
              The {isOwner ? "buyer" : "seller"} will be immediately notified of
              your offer.
            </Box>
            {errorMessage && (
              <Box my={2} textAlign="center">
                <ErrorMessage
                  title="Request Failed"
                  description={errorMessage}
                />
              </Box>
            )}
            <Box m={2} textAlign="center">
              <Box fontSize={18} mt={1}>
                Your Counteroffer Amount
              </Box>
              <Box
                fontSize={18}
                fontWeight={theme.typography.fontWeightMedium}
                m={1}
              >
                {formValues.amount}
              </Box>
            </Box>
            {formValues.updateListPrice && (
              <Box m={2} textAlign="center">
                <Box fontSize={18} mt={1}>
                  Your List Price
                </Box>
                <Box
                  fontSize={18}
                  fontWeight={theme.typography.fontWeightMedium}
                  m={1}
                >
                  {formValues.amount}
                </Box>
              </Box>
            )}
          </Typography>
          <Box m={2} textAlign="center">
            {!isOwner && (
              <TransactionFeeNotice
                isPurchasing={false}
                price={parseNumber(formValues.amount)}
              />
            )}
            <TermsAndConditions />
            <Box m={2}>
              <Button
                customColor="blue"
                type="submit"
                fullWidth
                size="large"
                variant="contained"
                onClick={handleMakeCounteroffer}
                id="confirm-counteroffer"
                disabled={isLoading}
                isLoading={isLoading}
              >
                Confirm Counteroffer
              </Button>
            </Box>
          </Box>
        </>
      )}
      {step === 2 && (
        <Typography component="div">
          <Box textAlign="center" mt={2}>
            <CheckCircleIcon
              style={{
                fontSize: circleIconSize.big,
                color: theme.custom.palette.blue.main,
              }}
            />
          </Box>
          <Box fontSize={15} textAlign="center" m={2}>
            The {isOwner ? "buyer" : "seller"} has been notified and will have{" "}
            {humanizedOfferExpirationDate} to respond to your offer.
          </Box>
        </Typography>
      )}
    </Modal>
  );
};

export default MakeCounterofferModal;
